import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';

interface TrackingResult {
  orderNumber: string;
  customerName: string;
  service: string;
  device: string;
  status: 'pending' | 'in-progress' | 'completed' | 'ready';
  statusText: string;
  date: string;
  estimatedCompletion: string;
  notes: string;
  timeline: {
    date: string;
    status: string;
    description: string;
    completed: boolean;
  }[];
}

export default function Tracking() {
  const [orderNumber, setOrderNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState<TrackingResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');

  // Mock data untuk demo
  const mockData: Record<string, TrackingResult> = {
    'BDS001': {
      orderNumber: 'BDS001',
      customerName: 'Ahmad Rizki',
      service: 'Servis Laptop',
      device: 'Asus ROG Strix G15',
      status: 'in-progress',
      statusText: 'Sedang Dikerjakan',
      date: '15 Maret 2024',
      estimatedCompletion: '17 Maret 2024',
      notes: 'Ganti LCD dan upgrade RAM 16GB',
      timeline: [
        {
          date: '15 Mar 2024, 09:00',
          status: 'Diterima',
          description: 'Laptop diterima dan dilakukan pengecekan awal',
          completed: true
        },
        {
          date: '15 Mar 2024, 14:00',
          status: 'Diagnosa',
          description: 'LCD rusak dan RAM perlu upgrade',
          completed: true
        },
        {
          date: '16 Mar 2024, 10:00',
          status: 'Pengerjaan',
          description: 'Proses penggantian LCD dan upgrade RAM',
          completed: true
        },
        {
          date: '17 Mar 2024',
          status: 'Quality Check',
          description: 'Pengujian dan quality control',
          completed: false
        },
        {
          date: '17 Mar 2024',
          status: 'Selesai',
          description: 'Siap diambil',
          completed: false
        }
      ]
    },
    'BDS002': {
      orderNumber: 'BDS002',
      customerName: 'Siti Nurhaliza',
      service: 'Desain Logo',
      device: '-',
      status: 'completed',
      statusText: 'Selesai',
      date: '10 Maret 2024',
      estimatedCompletion: '12 Maret 2024',
      notes: 'Logo untuk warung makan',
      timeline: [
        {
          date: '10 Mar 2024, 10:00',
          status: 'Order Diterima',
          description: 'Brief dan konsep diterima',
          completed: true
        },
        {
          date: '11 Mar 2024, 15:00',
          status: 'Desain Draft',
          description: '3 konsep desain telah dibuat',
          completed: true
        },
        {
          date: '12 Mar 2024, 11:00',
          status: 'Revisi',
          description: 'Revisi sesuai feedback klien',
          completed: true
        },
        {
          date: '12 Mar 2024, 16:00',
          status: 'Final',
          description: 'Desain final approved',
          completed: true
        },
        {
          date: '12 Mar 2024, 17:00',
          status: 'Selesai',
          description: 'File dikirim ke klien',
          completed: true
        }
      ]
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSearching(true);

    setTimeout(() => {
      const result = mockData[orderNumber.toUpperCase()];
      if (result) {
        setTrackingResult(result);
        setError('');
      } else {
        setTrackingResult(null);
        setError('Nomor order tidak ditemukan. Silakan periksa kembali atau hubungi kami via WhatsApp.');
      }
      setIsSearching(false);
    }, 1000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'in-progress':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'completed':
      case 'ready':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Header />
      
      <div className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="text-[#FF8A00] font-semibold text-sm uppercase tracking-wider bg-orange-50 px-4 py-2 rounded-full">
                Tracking Servis
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0B1F3B] mb-4">
              Lacak Status Servis Anda
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Masukkan nomor order untuk melihat status perkembangan servis Anda
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Search Form */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8 mb-8">
              <form onSubmit={handleSearch} className="space-y-6">
                <div>
                  <label htmlFor="orderNumber" className="block text-sm font-semibold text-[#0B1F3B] mb-2">
                    Nomor Order
                  </label>
                  <div className="flex gap-4">
                    <input
                      type="text"
                      id="orderNumber"
                      value={orderNumber}
                      onChange={(e) => setOrderNumber(e.target.value)}
                      required
                      className="flex-1 px-4 py-4 rounded-xl border border-gray-300 focus:border-[#FF8A00] focus:ring-2 focus:ring-[#FF8A00]/20 outline-none transition-all text-sm"
                      placeholder="Contoh: BDS001"
                    />
                    <button
                      type="submit"
                      disabled={isSearching}
                      className="bg-gradient-to-r from-[#0B1F3B] to-[#0d2847] hover:from-[#0d2847] hover:to-[#0B1F3B] text-white px-8 py-4 rounded-xl font-semibold transition-all flex items-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSearching ? (
                        <>
                          <i className="ri-loader-4-line text-xl animate-spin"></i>
                          Mencari...
                        </>
                      ) : (
                        <>
                          <i className="ri-search-line text-xl"></i>
                          Lacak
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">Nomor order dapat ditemukan di:</p>
                      <ul className="space-y-1 text-blue-700">
                        <li>• Nota/struk yang Anda terima saat menyerahkan barang</li>
                        <li>• Pesan WhatsApp konfirmasi dari kami</li>
                        <li>• Coba: BDS001 atau BDS002 untuk demo</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-xl flex-shrink-0">
                    <i className="ri-error-warning-line text-2xl text-red-600"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-red-800 mb-2">Order Tidak Ditemukan</h3>
                    <p className="text-red-700 mb-4">{error}</p>
                    <a
                      href="https://wa.me/6285868360910"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-fill text-xl"></i>
                      Hubungi Kami
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Tracking Result */}
            {trackingResult && (
              <div className="space-y-6">
                {/* Order Info */}
                <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-[#0B1F3B] to-[#0d2847] text-white p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold">Detail Order</h2>
                      <div className={`px-4 py-2 rounded-full border-2 font-bold ${getStatusColor(trackingResult.status)}`}>
                        {trackingResult.statusText}
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Nomor Order</p>
                        <p className="font-bold text-[#0B1F3B] text-lg">{trackingResult.orderNumber}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Nama Pelanggan</p>
                        <p className="font-bold text-[#0B1F3B] text-lg">{trackingResult.customerName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Jenis Layanan</p>
                        <p className="font-bold text-[#0B1F3B] text-lg">{trackingResult.service}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Perangkat</p>
                        <p className="font-bold text-[#0B1F3B] text-lg">{trackingResult.device}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Tanggal Masuk</p>
                        <p className="font-bold text-[#0B1F3B] text-lg">{trackingResult.date}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Estimasi Selesai</p>
                        <p className="font-bold text-[#0B1F3B] text-lg">{trackingResult.estimatedCompletion}</p>
                      </div>
                    </div>

                    {trackingResult.notes && (
                      <div className="mt-6 bg-orange-50 border border-orange-200 rounded-xl p-4">
                        <p className="text-sm text-gray-500 mb-1">Catatan</p>
                        <p className="text-[#0B1F3B]">{trackingResult.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8">
                  <h3 className="text-2xl font-bold text-[#0B1F3B] mb-8 flex items-center gap-3">
                    <i className="ri-time-line text-[#FF8A00]"></i>
                    Timeline Pengerjaan
                  </h3>

                  <div className="space-y-6">
                    {trackingResult.timeline.map((item, index) => (
                      <div key={index} className="flex gap-6">
                        <div className="flex flex-col items-center">
                          <div className={`w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0 ${
                            item.completed 
                              ? 'bg-[#FF8A00] text-white' 
                              : 'bg-gray-200 text-gray-400'
                          }`}>
                            {item.completed ? (
                              <i className="ri-check-line text-2xl"></i>
                            ) : (
                              <i className="ri-time-line text-2xl"></i>
                            )}
                          </div>
                          {index < trackingResult.timeline.length - 1 && (
                            <div className={`w-0.5 h-16 ${
                              item.completed ? 'bg-[#FF8A00]' : 'bg-gray-200'
                            }`}></div>
                          )}
                        </div>

                        <div className="flex-1 pb-8">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className={`font-bold text-lg ${
                              item.completed ? 'text-[#0B1F3B]' : 'text-gray-400'
                            }`}>
                              {item.status}
                            </h4>
                            <span className={`text-sm ${
                              item.completed ? 'text-gray-500' : 'text-gray-400'
                            }`}>
                              {item.date}
                            </span>
                          </div>
                          <p className={`${
                            item.completed ? 'text-gray-600' : 'text-gray-400'
                          }`}>
                            {item.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact CTA */}
                <div className="bg-gradient-to-r from-[#FF8A00] to-[#ff9d33] rounded-2xl p-6 text-white">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl flex-shrink-0">
                        <i className="ri-customer-service-2-line text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">Ada Pertanyaan?</h3>
                        <p className="text-white/90 text-sm">Hubungi kami untuk informasi lebih lanjut</p>
                      </div>
                    </div>
                    <a
                      href="https://wa.me/6285868360910"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-[#FF8A00] hover:bg-gray-100 px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-fill text-xl"></i>
                      Chat WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}